import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { coins, transactions, tags } from '../../utils/api';
import { customFields } from '../../utils/api/customFields';
import { useCoinForm } from './hooks/useCoinForm';

// Components
import FormSection from './components/FormSection';
import CertificationLookup from './components/CertificationLookup';
import BasicInformation from './components/BasicInformation';
import AdditionalInformation from './components/AdditionalInformation';
import PopulationAndPrice from './components/PopulationAndPrice';
import PurchaseDetails from './components/PurchaseDetails';
import ImagesSection from './components/ImagesSection';
import NotesSection from './components/NotesSection';
import ErrorMessage from './components/ErrorMessage';
import CertUpdateModal from './components/CertUpdateModal';
import CustomFieldsFormSection from '../CustomFields/CustomFieldsFormSection';

const CoinForm = ({ initialData, onSubmit }) => {
  const [showCertUpdateModal, setShowCertUpdateModal] = useState(false);
  const [pendingCertUpdate, setPendingCertUpdate] = useState(null);
  const navigate = useNavigate();
  const {
    formData,
    setFormData,
    loading,
    setLoading,
    error,
    setError,
    lookupLoading,
    setLookupLoading,
    services,
    focusedField,
    setFocusedField,
    handleChange,
    getDisplayValue,
    validateForm,
    handleLookup
  } = useCoinForm(initialData);

  const handleFocus = (e) => {
    const { name } = e.target;
    setFocusedField(name);
  };

  const handleBlur = () => {
    setFocusedField(null);
  };

  const isLookupDisabled = () => {
    if (!formData.certification_service_id || !formData.certification_number) {
      return true;
    }
    const selectedService = services.find(s => s.id === parseInt(formData.certification_service_id));
    return selectedService?.name === 'Other';
  };

  const applyLookupUpdate = (response, isPCGSNumber, selectedChanges) => {
    setFormData(prev => {
      const newData = {
        ...prev,
        // Preserve fields that shouldn't be overwritten
        user_coin_id: prev.user_coin_id,
        // Clear certification service if it's a PCGS number lookup (uncertified coin)
        certification_service_id: isPCGSNumber ? '' : prev.certification_service_id,
        // Only preserve certification_number if not a PCGS number lookup
        certification_number: isPCGSNumber ? '' : prev.certification_number,
        
        // Apply only selected changes
        ...selectedChanges.reduce((acc, change) => {
          acc[change.field] = change.newValue;
          return acc;
        }, {}),
        
        // Always update these fields as they are current values
        population: response.population || '',
        pop_higher: response.pop_higher || '',
        price_guide_value: response.price_guide_value || '',
        
        // Handle images if provided
        ...(response.images && {
          images: response.images
            .sort((a, b) => {
              // Sort by image type to ensure correct order (Image-1, Image-2, Image-3)
              const getTypeNumber = (type) => {
                const match = type.match(/Image-(\d+)/);
                return match ? parseInt(match[1]) : 999;
              };
              return getTypeNumber(a.type) - getTypeNumber(b.type);
            })
            .map(img => ({
              image_url: img.url,
              image_type: img.type
            }))
        }),
        
        // Set PCGS number if applicable
        ...(isPCGSNumber && { pcgs_number: response.pcgs_number || prev.certification_number })
      };

      return newData;
    });
  };

  const handleLookupWithModal = async () => {
    try {
      // Don't use the existing handleLookup since it automatically updates formData
      // Instead, perform the lookup manually without updating the form
      if (!formData.certification_service_id) {
        setError('Please select a certification service');
        return;
      }

      const selectedService = services.find(s => s.id === parseInt(formData.certification_service_id));
      if (!selectedService) {
        setError('Invalid certification service');
        return;
      }

      if (!formData.certification_number) {
        setError('Please enter a certification number');
        return;
      }

      setLookupLoading(true);
      setError(null);

      const certService = selectedService.name.toLowerCase();
      const isPCGSNumber = certService === 'pcgs' && /^\d{1,6}$/.test(formData.certification_number);

      // Import the certLookup API directly
      const { certLookup } = await import('../../utils/api');
      
      const response = await certLookup.lookup(
        certService,
        formData.certification_number,
        formData.ngc_grade,
        isPCGSNumber
      );

      // Add population fields with special mapping
      response.current_population = response.population;
      response.current_pop_higher = response.pop_higher;

      // Map CAC sticker data to form format
      if (response.cac_sticker?.type) {
        response.cac_sticker = response.cac_sticker.type.toLowerCase();
      }

      // Get all fields from the parser
      const fieldsToCheck = [
        'variety', 'metal', 'grade', 'mint_mark', 'country', 'denomination',
        'year', 'edge', 'composition', 'weight', 'diameter', 'mintage',
        'minor_variety', 'die_variety', 'reference_number', 'assayer',
        'pcgs_number', 'price_guide_value', 'population', 'pop_higher'
      ];

      // Helper function to check if a value is effectively empty
      const isEmpty = (value) => {
        return value === null || value === undefined || value === '' || value === 0;
      };

      // Helper function to normalize values for comparison
      const normalizeValue = (value, field) => {
        if (isEmpty(value)) return null;
        
        if (['weight', 'diameter'].includes(field)) {
          return Number(Number(value).toFixed(2));
        }
        if (['mintage', 'population', 'pop_higher'].includes(field)) {
          return Number(value);
        }
        if (['price_guide_value'].includes(field)) {
          return Number(Number(value).toFixed(2));
        }
        return value;
      };

      // Helper function to check if values are different
      const hasValueChanged = (oldValue, newValue, field) => {
        if (isEmpty(oldValue) && isEmpty(newValue)) return false;
        const normalizedOld = normalizeValue(oldValue, field);
        const normalizedNew = normalizeValue(newValue, field);
        return normalizedOld !== normalizedNew;
      };

      // Create changes array
      const changedFields = fieldsToCheck.map(field => ({
        field,
        oldValue: formData[field],
        newValue: response[field],
        hasChanged: hasValueChanged(formData[field], response[field], field),
        selected: true // Default to selected
      }));

      // Check if any fields have been changed and user has entered data
      const hasUserEnteredData = fieldsToCheck
        .filter(field => field !== 'grade')
        .some(field => !isEmpty(formData[field]));
      const hasChanges = changedFields.some(change => change.hasChanged);

      if (hasUserEnteredData && hasChanges) {
        setPendingCertUpdate({
          response,
          changedFields,
          isPCGSNumber
        });
        setShowCertUpdateModal(true);
        return;
      }

      // If no changes or no user data, just update all fields
      applyLookupUpdate(response, isPCGSNumber, changedFields);
    } catch (err) {
      console.error('Modal lookup error:', err);
      const errorData = err.response?.data;
      const errorMessage = errorData?.error 
        ? `${errorData.error}${errorData.details ? `: ${errorData.details}` : ''}`
        : 'Failed to fetch coin details. Please try again or enter details manually.';
      setError(errorMessage);
    } finally {
      setLookupLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    console.log('Form submission started');
    
    // Prevent double submission
    if (loading) {
      return;
    }

    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      setError(validationErrors.join(', '));
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const { status_name, user_coin_id, tags: selectedTags, contact_id, purchase_date, certification_number, certification_service_id, ...rest } = formData;
      console.log('Form data being submitted:', { ...rest, coin_details: formData.coin_details });
      const isPCGSNumberLookup = rest.pcgs_number && /^\d{1,6}$/.test(certification_number);
      
      // Only include certification service if there's a certification number
      // This ensures uncertified coins (looked up by PCGS number) don't have a TPG set
      const hasCertNumber = certification_number && certification_number.trim() !== '';
      
      const dataToSubmit = {
        ...rest,
        status: status_name,
        user_coin_id,
        // Only include cert number and service if there's actually a cert number
        // For PCGS number lookups or uncertified coins, these should be empty
        ...(hasCertNumber && !isPCGSNumberLookup ? {
          certification_number,
          certification_service_id
        } : {
          certification_number: null,
          certification_service_id: null
        }),
        // Include custom field values if they exist
        ...(formData.custom_field_values && {
          custom_field_values: formData.custom_field_values
        }),
        // Ensure purchase_date is sent without timezone conversion
        purchase_date: purchase_date ? (() => {
          try {
            // If purchase_date is already a valid date string, use it
            const date = new Date(purchase_date);
            if (!isNaN(date.getTime())) {
              // Set to noon UTC to avoid timezone issues
              date.setUTCHours(12, 0, 0, 0);
              return date.toISOString();
            }
            return null;
          } catch (e) {
            console.error('Invalid purchase_date:', purchase_date, e);
            return null;
          }
        })() : null
      };
      
      if (selectedTags?.length > 0) {
        dataToSubmit.tagIds = selectedTags.map(tag => tag.id);
      }

      // Convert numeric fields
      ['purchase_price', 'list_price', 'grading_cost', 'shipping_cost', 'population', 'pop_higher'].forEach(field => {
        if (formData[field]) {
          const cleanValue = formData[field].toString().replace(/,/g, '');
          dataToSubmit[field] = parseFloat(cleanValue);
        }
      });

      // Preserve mintage as text
      if (formData.mintage) {
        dataToSubmit.mintage = formData.mintage;
      }

      const savedCoin = await coins.create(dataToSubmit);

      // Add tags if needed
      if (selectedTags?.length > 0) {
        await tags.addTagsToCoin(savedCoin.id, selectedTags.map(tag => tag.id));
      }

      // Save custom field values if they exist
      if (formData.custom_field_values && Object.keys(formData.custom_field_values).length > 0) {
        await customFields.setValues(savedCoin.id, formData.custom_field_values);
      }

      // Create purchase transaction if we have a seller
      if (contact_id) {
        try {
          const transactionData = {
            contact_id: contact_id,
            coin_prices: {
              [savedCoin.id]: {
                price: formData.purchase_price ? parseFloat(formData.purchase_price) : null,
                fees: 0
              }
            },
            transaction_date: formData.purchase_date === '' ? null : formData.purchase_date,
            type: 'purchase',
            notes: ''
          };
          await transactions.create(transactionData);
        } catch (err) {
          console.error('Failed to create purchase transaction:', err);
        }
      }

      navigate(`/coins/${savedCoin.id}`);
    } catch (err) {
      console.error('Form submission error:', err);
      setError(err.message || 'Failed to save coin. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <form 
        onSubmit={handleSubmit} 
        className="space-y-4"
        onKeyDown={(e) => {
          if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') {
            e.preventDefault();
          }
        }}
      >
      <ErrorMessage error={error} />

      <div className="space-y-6">
        <CertificationLookup
          services={services}
          formData={formData}
          handleChange={handleChange}
          handleLookup={handleLookupWithModal}
          lookupLoading={lookupLoading}
          setError={setError}
          setFormData={setFormData}
          isLookupDisabled={isLookupDisabled}
        />

        <div className="flex flex-col md:flex-row md:flex-wrap gap-6">
          <FormSection title="Basic Information">
            <BasicInformation
              formData={formData}
              handleChange={handleChange}
              handleFocus={handleFocus}
              handleBlur={handleBlur}
              getDisplayValue={getDisplayValue}
              setFormData={setFormData}
            />
          </FormSection>

          <FormSection title="Additional Information">
            <AdditionalInformation
              formData={formData}
              handleChange={handleChange}
              handleFocus={handleFocus}
              handleBlur={handleBlur}
              getDisplayValue={getDisplayValue}
            />
          </FormSection>

          <FormSection title="Population & Price Details">
            <PopulationAndPrice
              formData={formData}
              handleChange={handleChange}
              handleFocus={handleFocus}
              handleBlur={handleBlur}
              getDisplayValue={getDisplayValue}
            />
          </FormSection>

          <FormSection title="Purchase Details">
            <PurchaseDetails
              formData={formData}
              handleChange={handleChange}
              handleFocus={handleFocus}
              handleBlur={handleBlur}
              getDisplayValue={getDisplayValue}
              setFormData={setFormData}
            />
          </FormSection>

          {formData.images && formData.images.length > 0 && (
            <FormSection title="Images">
              <ImagesSection images={formData.images} />
            </FormSection>
          )}

          <FormSection title="Custom Fields">
            <CustomFieldsFormSection
              onChange={(values) => {
                setFormData(prev => ({
                  ...prev,
                  custom_field_values: values
                }));
              }}
              className="space-y-4"
            />
          </FormSection>

          <FormSection title="Notes" fullWidth={true}>
            <NotesSection
              formData={formData}
              handleChange={handleChange}
            />
          </FormSection>
        </div>

        <div className="pt-5">
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => navigate('/inventory')}
              className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className={`ml-3 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white ${
                loading ? 'bg-indigo-400' : 'bg-indigo-600 hover:bg-indigo-700'
              } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500`}
            >
              {loading ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </div>
      </form>

      {showCertUpdateModal && (
        <CertUpdateModal
          isOpen={true}
          onClose={() => {
            setShowCertUpdateModal(false);
            setPendingCertUpdate(null);
          }}
          changes={pendingCertUpdate.changedFields}
          onApply={(selectedChanges) => {
            applyLookupUpdate(
              pendingCertUpdate.response,
              pendingCertUpdate.isPCGSNumber,
              selectedChanges
            );
            setShowCertUpdateModal(false);
            setPendingCertUpdate(null);
          }}
        />
      )}
    </>
  );
};

export default CoinForm;
